import { when } from 'mobx';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type {
  BlackbaudRaisersEdgeContactMappingStore,
  BlackbaudRaisersEdgeIntegration,
  IBlackbaudContactMapping,
  IBlackbaudContactMappingUpdate,
} from '@feathr/blackbox';
import { FieldCollection } from '@feathr/blackbox';
import type { IIntegrationUIFieldOption, IIntegrationUIMapping } from '@feathr/components';
import { useStore } from '@feathr/extender/state';

import { useLazyOptions } from '../useLazyOptions';
import type { IMappingChanges } from './RaisersEdgeMappingsTab.utils';
import {
  getAddedMappingChanges,
  getFeathrFieldOptions,
  getMappingChanges,
  hasMappingChanges,
  lockManagedFeathrFields,
  orderMappingUpdates,
  toMappingUpdate,
  toUIMapping,
} from './RaisersEdgeMappingsTab.utils';

export interface IRaisersEdgeMappingDraft {
  /** The table rows: the staged edits, or the saved mappings when nothing is staged. */
  rows: readonly IIntegrationUIMapping[];
  /** Stages the table's new rows. */
  setRows: (rows: IIntegrationUIMapping[]) => void;
  /** Whether the staged rows differ from the saved mappings. */
  isDirty: boolean;
  /** Why the staged rows can't be saved yet. */
  saveDisabledReason?: string;
  /** Saves the staged rows, then drops them for the reloaded mappings. Rejects with the first failure. */
  save: () => Promise<void>;
}

/** Runs the requests one at a time. Every request runs even if one fails; failures are collected. */
function runInSequence(
  requests: ReadonlyArray<() => Promise<unknown>>,
  failures: unknown[],
): Promise<void> {
  return requests.reduce<Promise<void>>(
    (previous, request) =>
      previous.then(request).then(
        () => undefined,
        (error: unknown) => {
          failures.push(error);
        },
      ),
    Promise.resolve(),
  );
}

/**
 * Applies the changes one request at a time, in phase order, so no request takes a Feathr field
 * that another still holds. Every request runs even if one fails; the first failure is rethrown.
 */
async function applyMappingChanges(
  store: BlackbaudRaisersEdgeContactMappingStore,
  saved: readonly IBlackbaudContactMapping[],
  changes: IMappingChanges,
): Promise<void> {
  const failures: unknown[] = [];
  const created: IBlackbaudContactMapping[] = [];
  const update = (change: IBlackbaudContactMappingUpdate) => (): Promise<unknown> =>
    store.update(change);
  const clear = (id: IBlackbaudContactMapping['id']): (() => Promise<unknown>) =>
    update(toMappingUpdate(id, null));

  await runInSequence(
    [
      ...changes.remove.map((id) => (): Promise<unknown> => store.remove(id)),
      ...changes.clear.map(clear),
      ...changes.add.map(({ key }) => async (): Promise<void> => {
        created.push(...(await store.add([key])));
      }),
    ],
    failures,
  );
  const added = getAddedMappingChanges(created, changes);
  const assign = orderMappingUpdates([...added.assign, ...changes.assign], [...saved, ...created]);
  await runInSequence([...added.clear.map(clear), ...assign.map(update)], failures);
  if (failures.length) {
    throw failures[0];
  }
}

/**
 * Mapping edits staged for the page's Save & Sync. Reads the integration's mapping store; the
 * Mappings tab owns its load. Lives on the page so staged edits survive a tab switch.
 */
export function useRaisersEdgeMappingDraft(
  integration: BlackbaudRaisersEdgeIntegration | undefined,
): IRaisersEdgeMappingDraft {
  const { t } = useTranslation();
  // Staged rows belong to one integration, so a replaced model doesn't inherit them.
  const [draft, setDraft] = useState<{ integrationId: string; rows: IIntegrationUIMapping[] }>();
  const store = integration?.contactMappingStore;
  const saved = store?.sortedMappings ?? [];
  const stagedRows = draft && draft.integrationId === integration?.id ? draft.rows : undefined;
  const rows = lockManagedFeathrFields(
    stagedRows ?? saved.map((mapping) => toUIMapping(mapping, t)),
    saved,
  );
  const changes = getMappingChanges(saved, rows);
  const isDirty = !!stagedRows && hasMappingChanges(changes);
  const saveDisabledReason =
    isDirty && rows.some(({ crmFieldKey }) => crmFieldKey === null)
      ? t('Choose a Blackbaud field for each new mapping, or remove it.')
      : undefined;

  function setRows(nextRows: IIntegrationUIMapping[]): void {
    if (integration) {
      setDraft({ integrationId: integration.id, rows: nextRows });
    }
  }

  async function save(): Promise<void> {
    if (!store || !isDirty) {
      return;
    }
    try {
      await applyMappingChanges(store, saved, changes);
    } finally {
      // The store reloads after every change, so it holds whatever the server saved.
      setDraft(undefined);
    }
  }

  return { isDirty, rows, save, saveDisabledReason, setRows };
}

/**
 * Reloads the saved mappings on each poll, so each row's last sync stays current. Staged edits are
 * kept. Skips a store the Mappings tab has not loaded yet.
 */
export function useRaisersEdgeMappingsRefresh(
  integration: BlackbaudRaisersEdgeIntegration | undefined,
  pollCount: number,
): void {
  useEffect(() => {
    const store = integration?.contactMappingStore;
    if (pollCount && store?.isFetched) {
      void store.refresh();
    }
  }, [pollCount]);
}

/** Feathr person fields a mapping can target, loaded the first time `enabled` is true. */
export function useFeathrFieldOptions(
  enabled: boolean,
): readonly IIntegrationUIFieldOption[] | undefined {
  const { t } = useTranslation();
  const { CustomFields } = useStore();

  async function load(): Promise<IIntegrationUIFieldOption[]> {
    const customFields = CustomFields.list({
      filters: { collection: FieldCollection.Person, is_archived__ne: true },
      pagination: { page_size: 1000 },
    });
    await when(() => !customFields.isPending);
    if (customFields.isErrored) {
      throw customFields.error;
    }
    return getFeathrFieldOptions(customFields.models);
  }

  return useLazyOptions({
    enabled,
    errorLabel: (error) => t('Failed to load Feathr fields:\n{{- error}}', { error }),
    load,
  });
}
